import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

const TariffPreviewTable = ({ data, onUpdate }) => {
  const [editingField, setEditingField] = useState(null);
  const [localData, setLocalData] = useState(data);

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const handleStartEdit = (field) => {
    setEditingField(field);
  };

  const handleCancelEdit = () => {
    setLocalData(data);
    setEditingField(null);
  };

  const handleSaveEdit = () => {
    onUpdate(localData);
    setEditingField(null);
  };

  const handleChange = (field, value) => {
    setLocalData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNestedChange = (parent, field, value) => {
    setLocalData((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value,
      },
    }));
  };

  const renderEditableField = (field, value, label, type = "text") => {
    const isEditing = editingField === field;

    return (
      <TableRow>
        <TableCell component="th" scope="row" sx={{ fontWeight: 600, width: "30%" }}>
          {label}
        </TableCell>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {isEditing ? (
              <>
                <TextField
                  fullWidth
                  size="small"
                  value={value || ""}
                  onChange={(e) => handleChange(field, e.target.value)}
                  type={type}
                  autoFocus
                />
                <IconButton size="small" color="primary" onClick={handleSaveEdit}>
                  <CheckIcon />
                </IconButton>
                <IconButton size="small" color="error" onClick={handleCancelEdit}>
                  <CloseIcon />
                </IconButton>
              </>
            ) : (
              <>
                <Typography sx={{ flex: 1 }}>{value || "-"}</Typography>
                <Tooltip title="Modifica">
                  <IconButton size="small" onClick={() => handleStartEdit(field)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  const renderSelectField = (field, value, label, options) => {
    const isEditing = editingField === field;

    return (
      <TableRow>
        <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
          {label}
        </TableCell>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {isEditing ? (
              <>
                <TextField
                  fullWidth
                  select
                  size="small"
                  value={value || ""}
                  onChange={(e) => handleChange(field, e.target.value)}
                  autoFocus
                >
                  {options.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </TextField>
                <IconButton size="small" color="primary" onClick={handleSaveEdit}>
                  <CheckIcon />
                </IconButton>
                <IconButton size="small" color="error" onClick={handleCancelEdit}>
                  <CloseIcon />
                </IconButton>
              </>
            ) : (
              <>
                <Typography sx={{ flex: 1 }}>{value || "-"}</Typography>
                <Tooltip title="Modifica">
                  <IconButton size="small" onClick={() => handleStartEdit(field)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell colSpan={2}>
              <Typography variant="h6">Dati Tariffa</Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {renderEditableField("fornitore", localData.fornitore, "Fornitore")}
          {renderEditableField("nomeOfferta", localData.nomeOfferta, "Nome Offerta")}
          {renderSelectField("tipo", localData.tipo, "Tipo Tariffa", [
            "luce",
            "gas",
            "dual",
          ])}

          {/* Prezzo Materia Prima */}
          <TableRow>
            <TableCell colSpan={2} sx={{ bgcolor: "action.hover" }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Prezzo Materia Prima
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ pl: 4 }}>Valore</TableCell>
            <TableCell>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {editingField === "prezzoMateriaPrima.valore" ? (
                  <>
                    <TextField
                      size="small"
                      type="number"
                      value={localData.prezzoMateriaPrima?.valore || ""}
                      onChange={(e) =>
                        handleNestedChange("prezzoMateriaPrima", "valore", e.target.value)
                      }
                      autoFocus
                    />
                    <IconButton size="small" color="primary" onClick={handleSaveEdit}>
                      <CheckIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={handleCancelEdit}>
                      <CloseIcon />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <Typography sx={{ flex: 1 }}>
                      {localData.prezzoMateriaPrima?.valore || "-"}
                    </Typography>
                    <Tooltip title="Modifica">
                      <IconButton
                        size="small"
                        onClick={() => handleStartEdit("prezzoMateriaPrima.valore")}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ pl: 4 }}>Unità</TableCell>
            <TableCell>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {editingField === "prezzoMateriaPrima.unita" ? (
                  <>
                    <TextField
                      select
                      size="small"
                      value={localData.prezzoMateriaPrima?.unita || ""}
                      onChange={(e) =>
                        handleNestedChange("prezzoMateriaPrima", "unita", e.target.value)
                      }
                      autoFocus
                      sx={{ minWidth: 150 }}
                    >
                      <MenuItem value="€/kWh">€/kWh</MenuItem>
                      <MenuItem value="€/Smc">€/Smc</MenuItem>
                    </TextField>
                    <IconButton size="small" color="primary" onClick={handleSaveEdit}>
                      <CheckIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={handleCancelEdit}>
                      <CloseIcon />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <Typography sx={{ flex: 1 }}>
                      {localData.prezzoMateriaPrima?.unita || "-"}
                    </Typography>
                    <Tooltip title="Modifica">
                      <IconButton
                        size="small"
                        onClick={() => handleStartEdit("prezzoMateriaPrima.unita")}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ pl: 4 }}>Fasce Orarie</TableCell>
            <TableCell>
              <Typography>
                {localData.prezzoMateriaPrima?.fasceOrarie ? "Sì" : "No"}
              </Typography>
            </TableCell>
          </TableRow>

          {renderEditableField("spread", localData.spread, "Spread (€/kWh o €/Smc)", "number")}

          {/* PCV */}
          <TableRow>
            <TableCell colSpan={2} sx={{ bgcolor: "action.hover" }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Prezzo Commerciale Variabile (PCV)
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ pl: 4 }}>Mensile</TableCell>
            <TableCell>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {editingField === "pcv.valoreMensile" ? (
                  <>
                    <TextField
                      size="small"
                      type="number"
                      value={localData.pcv?.valoreMensile || ""}
                      onChange={(e) => handleNestedChange("pcv", "valoreMensile", e.target.value)}
                      autoFocus
                    />
                    <IconButton size="small" color="primary" onClick={handleSaveEdit}>
                      <CheckIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={handleCancelEdit}>
                      <CloseIcon />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <Typography sx={{ flex: 1 }}>
                      {localData.pcv?.valoreMensile
                        ? `€ ${localData.pcv.valoreMensile}`
                        : "-"}
                    </Typography>
                    <Tooltip title="Modifica">
                      <IconButton
                        size="small"
                        onClick={() => handleStartEdit("pcv.valoreMensile")}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ pl: 4 }}>Annuo</TableCell>
            <TableCell>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {editingField === "pcv.valoreAnnuo" ? (
                  <>
                    <TextField
                      size="small"
                      type="number"
                      value={localData.pcv?.valoreAnnuo || ""}
                      onChange={(e) => handleNestedChange("pcv", "valoreAnnuo", e.target.value)}
                      autoFocus
                    />
                    <IconButton size="small" color="primary" onClick={handleSaveEdit}>
                      <CheckIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={handleCancelEdit}>
                      <CloseIcon />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <Typography sx={{ flex: 1 }}>
                      {localData.pcv?.valoreAnnuo ? `€ ${localData.pcv.valoreAnnuo}` : "-"}
                    </Typography>
                    <Tooltip title="Modifica">
                      <IconButton
                        size="small"
                        onClick={() => handleStartEdit("pcv.valoreAnnuo")}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            </TableCell>
          </TableRow>

          {/* Confidence Score */}
          <TableRow>
            <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
              Confidence Score
            </TableCell>
            <TableCell>
              <Typography>{localData.confidenceScore}%</Typography>
            </TableCell>
          </TableRow>

          {/* Needs Review */}
          <TableRow>
            <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
              Richiede Revisione
            </TableCell>
            <TableCell>
              <Typography color={localData.needsReview ? "error" : "success"}>
                {localData.needsReview ? "Sì" : "No"}
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TariffPreviewTable;
